import { useMemo, useRef, useState, type ComponentProps } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { Button } from '../../platform/primitives/Button'
import { BackLink } from '../../platform/primitives/BackLink'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { MerchantAvatar } from '../../platform/primitives/MerchantAvatar'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { useAccounts } from '../accounts/useAccounts'
import { useCategories } from '../categories/useCategories'
import { activityLog } from './activityLog'
import { DeleteTransactionsDialog } from './DeleteTransactionsDialog'
import { EditPanel } from './EditPanel'
import { RuleFlow, rulePath, useRuleCap, useRuleThatSet, type RuleFlowHandle } from '../rules/RuleFlow'
import { MakeItARule, isDismissed } from './MakeItARule'
import { MerchantStats } from './MerchantStats'
import { RecurringRow } from './RecurringRow'
import type { Txn } from './transactionWire'
import { accountLabel, avatarOf, longDate } from './txnDisplay'
import { useTransaction } from './useTransaction'
import './transactions.css'
import './transactionDetail.css'

const SKELETON = (
  <div className="txn-detail__skeleton">
    <Skeleton width="60%" height={28} />
    <Skeleton width="40%" height={16} />
    <Skeleton width="100%" height={120} />
  </div>
)

/**
 * True when a `/cashflow/…` or `/budget/…` path could resolve outside its prefix: a backslash (some
 * parsers read it as `/`), or a `.`/`..` segment, literal or percent-encoded, which the
 * URL parser collapses (`/cashflow/%2e%2e/admin` is `/admin`). An encoded `%2F` inside a
 * segment stays inside it, so payer names keep working.
 */
function escapesPrefix(path: string): boolean {
  if (path.includes('\\')) return true
  const pathname = path.split(/[?#]/, 1)[0] ?? ''
  return pathname.split('/').some((seg) => /^(\.|%2e){1,2}$/i.test(seg))
}

/**
 * Where Back goes, from what the row link carried in `location.state.from`: a `?search`
 * (the /transactions list, restoring its filtered view) or a `/cashflow/…` drill-down URL.
 * Anything else, including `//host` and `https://…`, is ignored: state is not trusted
 * as a redirect target.
 */
export function backTarget(state: unknown): { to: string; label: string } {
  const from = typeof state === 'object' && state !== null && 'from' in state ? state.from : undefined
  if (typeof from === 'string') {
    if (from.startsWith('?')) return { to: `/transactions${from}`, label: 'All transactions' }
    if (from.startsWith('/cashflow/') && !escapesPrefix(from)) return { to: from, label: 'Back to Cashflow' }
    if (from.startsWith('/budget/') && !escapesPrefix(from)) return { to: from, label: 'Back to envelope' }
  }
  return { to: '/transactions', label: 'All transactions' }
}

const ACTIVITY_DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

export function TransactionDetail() {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const back = backTarget(location.state)
  const { missing, ...loaded } = useTransaction(id)
  const state =
    missing && loaded.status === 'error'
      ? { ...loaded, recovery: {
            label: back.label === 'All transactions' ? 'Back to transactions' : back.label,
            onAct: () => navigate(back.to),
          } }
      : loaded

  return (
    <div className="txn-detail">
      <BackLink to={back.to}>{back.label}</BackLink>
      <Async
        surface="transaction-detail"
        state={state}
        skeleton={SKELETON}
        empty={{ title: 'Transaction not found.' }}
      >
        {(txn) => <Loaded key={txn.id} txn={txn} onDeleted={() => navigate(back.to, { replace: true })} />}
      </Async>
    </div>
  )
}

/** The card, told whether a new rule may be made. Its own component so the rules
 *  list is fetched only once a correction has put the card on screen. */
function CappedMakeItARule(props: Omit<ComponentProps<typeof MakeItARule>, 'blocked'>) {
  const { atCap, reason } = useRuleCap()
  return <MakeItARule {...props} blocked={atCap ? reason : undefined} />
}

/**
 * The detail screen (M6), as the owner chose it on epic 7 (2026-10-02), which
 * supersedes the `is.txnDetail` artboard for this screen: a full-width hero,
 * then a "Details" column beside a rail of the merchant's figures, the rule
 * offer and the activity timeline. The hero does NOT repeat the editor — the
 * category, tags and owner are edited in Details, so only the status chips the
 * panel cannot edit (Pending, Reviewed) appear there.
 *
 * The rail needs the room, so the two-column grid is for `desktop` and up;
 * below that it is one column, and the rail's cards follow the details in the
 * order a phone reader wants them: the rule first (it answers the edit they
 * just made), then the figures, then the activity.
 */
function Loaded({ txn, onDeleted }: { txn: Txn; onDeleted: () => void }) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const deleteButton = useRef<HTMLButtonElement>(null)
  const accountsState = useAccounts()
  const categoriesState = useCategories()
  const bp = useBreakpoint()
  const railed = bp === 'desktop' || bp === 'wide'
  const phone = bp === 'phone'
  const account =
    accountsState.status === 'success' || accountsState.status === 'stale'
      ? accountsState.data.find((a) => a.id === txn.accountId)
      : undefined
  // Which rule set the category, asked only when the row says a rule did. Unknown or none: the generic words.
  const setBy = useRuleThatSet(txn.id, 'category_id', txn.categorySource === 'rule')
  const log = useMemo(() => activityLog(txn, setBy ?? null), [txn, setBy])
  // "This year" must not change under a mounted screen.
  const [today] = useState(() => new Date())
  // Tags already on the row when the screen opened; the rule offers only the ones added since.
  const [initialTags] = useState(() => txn.tags)
  const addedTags = txn.tags.filter((t) => !initialTags.includes(t))
  /** Set by a category change the server confirmed (Q3). `seq` makes each one a fresh appearance of the card. */
  const [correction, setCorrection] = useState<{ categoryId: string; seq: number } | null>(null)
  /** The rule flow fetches the user's rules, so it is mounted only once a correction has been offered one. */
  const [offered, setOffered] = useState(false)
  const ruleFlow = useRef<RuleFlowHandle>(null)
  const categoryName =
    correction !== null && categoriesState.status === 'success'
      ? categoriesState.data.find((c) => c.id === correction.categoryId)?.name
      : undefined

  const rule = correction !== null && categoryName !== undefined && !isDismissed(txn.merchant) && (
    <CappedMakeItARule
      key={correction.seq}
      merchant={txn.merchant}
      categoryId={correction.categoryId}
      categoryName={categoryName}
      addedTags={addedTags}
      onCreate={(draft, after, onWrittenUncounted) => ruleFlow.current?.saveDirect(draft, after, onWrittenUncounted) ?? Promise.resolve()}
      onClose={() => setCorrection(null)}
    />
  )
  const stats = <MerchantStats merchantId={txn.merchantId} name={txn.merchant} today={today} />
  const activity = (
    <Panel className="txn-detail__activity">
      <h3>Activity</h3>
      <ol className="txn-timeline">
        {log.map((e) => (
          <li key={e.key} className="txn-timeline__entry">
            <span className="txn-timeline__dot" aria-hidden="true" />
            <span className="txn-timeline__text">
              {e.rule === undefined ? (
                e.text
              ) : (
                <>
                  Auto-rule “{e.rule.ruleId === null ? e.rule.name : <InlineLink to={rulePath(e.rule.ruleId)}>{e.rule.name}</InlineLink>}” set the
                  category
                </>
              )}
            </span>
            {e.at !== null && <span className="txn-detail__when">{ACTIVITY_DATE.format(new Date(e.at))}</span>}
          </li>
        ))}
      </ol>
    </Panel>
  )

  return (
    <div className="txn-detail__body">
      <Panel className={phone ? 'txn-detail__header txn-detail__header--compact' : 'txn-detail__header'}>
        <div className="txn-detail__id">
          <MerchantAvatar merchant={avatarOf(txn)} size={phone ? 'row' : 'detail'} />
          <div className="txn-detail__title">
            <h2>{txn.merchant}</h2>
            <p className="txn-detail__meta">
              {longDate(txn.date)}
              {account ? ` · ${accountLabel(account)}` : ''}
            </p>
            {txn.reviewedAt !== null && (
              <div className="txn-detail__chips">
                <span className="txn-chip">Reviewed</span>
              </div>
            )}
          </div>
        </div>
        <div className="txn-detail__amount">
          <Money cents={txn.amountCents} currency={txn.currency} variant="hero" kind="flow" />
          <p className="txn-detail__status">
            {txn.isPending ? <span className="txn-chip">Pending</span> : 'Cleared'}
            {` · ${txn.currency}`}
          </p>
          <Button ref={deleteButton} type="button" size="page" variant="destructive" onClick={() => setConfirmingDelete(true)}>
            Delete
          </Button>
        </div>
      </Panel>

      {confirmingDelete && (
        <DeleteTransactionsDialog ids={[txn.id]} restoreFocusTo={deleteButton} onClose={() => setConfirmingDelete(false)} onDeleted={onDeleted} />
      )}

      <div className={railed ? 'txn-detail__grid txn-detail__grid--rail' : 'txn-detail__grid'}>
        <div className="txn-detail__main">
          <EditPanel
            txn={txn}
            onCategoryChanged={(_, next) => {
              if (next !== null) setOffered(true)
              setCorrection((c) => (next === null ? null : { categoryId: next, seq: (c?.seq ?? 0) + 1 }))
            }}
          />
          <RecurringRow txn={txn} />
        </div>
        <div className={railed ? 'txn-detail__rail txn-detail__rail--sticky' : 'txn-detail__rail'}>
          {railed ? (
            <>
              {stats}
              {rule}
            </>
          ) : (
            <>
              {rule}
              {stats}
            </>
          )}
          {activity}
          {offered && <RuleFlow ref={ruleFlow} />}
        </div>
      </div>
    </div>
  )
}
